import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadPlayers, searchPlayers } from "../data/playerLoader";
import {
  extractAllBidAmounts,
  extractBidAmount,
  deduplicateRepeatedDigits,
  formatBidAmount,
  normalizeTranscript,
  resolveBidAmount,
  MAX_BID_AMOUNT,
  REPEATED_DIGIT_PATTERN,
  REPEATED_WORD_PATTERN,
} from "../data/bidParser";
import useDebugLog from "../hooks/useDebugLog";
import useBidTimer from "../hooks/useBidTimer";
import useAuctionAudio from "../hooks/useAuctionAudio";
import PlayerSearch from "./PlayerSearch";

// ── Timing constants ─────────────────────────────────────────────────

const BID_WINDOW_MS = 5000;
const DRAIN_TIMEOUT_MS = 2000;
const HOT_AUCTION_BID_THRESHOLD = 5;
const HOT_AUCTION_EXTENSION_MS = 1500;
const ANNOUNCEMENT_PAUSE_MS = 3000;
const BID_FEEDBACK_DISPLAY_MS = 1200;
const INTERIM_BID_COOLDOWN_MS = 1500;
// How long an interim bid is buffered before committing. The recognizer
// refines numbers mid-utterance ("40" → "44"); waiting a beat means one
// commit and one ding instead of two.
const INTERIM_BID_COMMIT_DELAY_MS = 600;
const RAPID_RESTART_THRESHOLD_MS = 500;
const MAX_RAPID_RESTARTS = 3;
const NON_FATAL_RECOGNITION_ERRORS = new Set(["aborted", "no-speech"]);

// ── Browser helpers ──────────────────────────────────────────────────

const getSpeechRecognition = () =>
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

// ── UI state configuration ───────────────────────────────────────────
// These are pure data tables keyed by uiState. They're defined outside
// the component so they're allocated once. Dynamic bits (like player name)
// are filled in at render time via a small helper.

const STATIC_UI_STATE_COPY = {
  waiting_opening_bid: {
    badgeText: "BIDDING OPEN",
    headlineText: "Waiting For Opening Bid",
    subtext: "The auction clock starts after the first valid bid.",
    timerLabel: "Waiting For First Bid",
    timerCaption: "No countdown yet.",
    primaryButtonText: "Stop Listening",
  },
  listening: {
    badgeText: "BIDDING OPEN",
    headlineText: "Listening For Bids",
    subtext: "Waiting for the next higher bid.",
    timerLabel: "Time Until Going Once",
    timerCaption: "Waiting for next higher bid.",
    primaryButtonText: "Stop Listening",
  },
  resetting_bid: {
    badgeText: "BID RESET",
    headlineText: "Correcting Bid",
    subtext: "Awaiting the corrected bid amount.",
    timerLabel: "Bid Reset",
    timerCaption: "Timer restarts after the corrected bid.",
    primaryButtonText: "Stop Listening",
  },
  going_once: {
    badgeText: "FINAL CALL",
    headlineText: "Going Once",
    subtext: "A higher bid will restart the auction.",
    timerLabel: "Time Until Going Twice",
    timerCaption: "Closing interval in progress.",
    primaryButtonText: "Stop Listening",
  },
  going_twice: {
    badgeText: "FINAL CALL",
    headlineText: "Going Twice",
    subtext: "A higher bid will restart the auction before sold.",
    timerLabel: "Time Until Sold",
    timerCaption: "Closing interval in progress.",
    primaryButtonText: "Stop Listening",
  },
  error: {
    badgeText: "MIC ERROR",
    headlineText: "Unable To Continue",
    timerLabel: "Unavailable",
    timerCaption: "The auction clock is unavailable.",
    primaryButtonText: "Start Listening",
  },
  unsupported: {
    badgeText: "UNAVAILABLE",
    headlineText: "Speech Recognition Not Supported",
    subtext: "This browser cannot run live voice bidding.",
    timerLabel: "Unavailable",
    timerCaption: "Live bidding is not supported here.",
    primaryButtonText: "Not Supported",
  },
};

const STATIC_BID_DISPLAY = {
  waiting_opening_bid: { bidLabel: "Current Bid", bidValue: "Opening Bid Needed" },
  resetting_bid: { bidLabel: "Current Bid", bidValue: "Type or speak correct current bid" },
  unsupported: { bidLabel: "Last Bid", bidValue: "Unavailable" },
};

const STATIC_FOOTER_COPY = {
  waiting_opening_bid: { footerLabel: "Next Step", footerMessage: "Say the opening bid to begin the auction clock." },
  resetting_bid: { footerLabel: "Bid Reset", footerMessage: "Say or type the correct current bid to continue the auction." },
  listening: { footerLabel: "Bidding Tip", footerMessage: "Only higher spoken bids will be accepted." },
  bid_received: { footerLabel: "Bidding Tip", footerMessage: "Only higher spoken bids will be accepted." },
  going_once: { footerLabel: "Final Call", footerMessage: "A higher bid will restart the auction." },
  going_twice: { footerLabel: "Final Call", footerMessage: "A higher bid will restart the auction before sold." },
  error: { footerLabel: "Recovery", footerMessage: "Check microphone permissions and try again." },
  unsupported: { footerLabel: "Browser Support", footerMessage: "Try a supported browser on this device." },
};

const STATE_TONE_MAP = {
  nominating: "nominating",
  idle: "idle",
  waiting_opening_bid: "waiting",
  resetting_bid: "waiting",
  listening: "active",
  bid_received: "active",
  going_once: "final-call",
  going_twice: "final-call",
  sold: "sold",
  error: "error",
  unsupported: "unsupported",
};

// ── Component ────────────────────────────────────────────────────────

export default function Auctioneer() {
  // ── Hooks ─────────────────────────────────────────────────────────
  const { debugMode, debugEvents, pushDebugEvent } = useDebugLog();
  const { timerRemainingMs, timerTotalMs, startVisualTimer, clearVisualTimer } = useBidTimer();
  const {
    playAuctionClip,
    stopAuctionClip,
    playBidDing,
    unlockAudioContext,
    resetClipAvailability,
  } = useAuctionAudio(pushDebugEvent);

  // ── State ─────────────────────────────────────────────────────────
  const [listening, setListening] = useState(false);
  const [isStartingListening, setIsStartingListening] = useState(false);
  const [lastBid, setLastBid] = useState(null);
  const [uiState, setUiState] = useState("nominating");
  const [errorMessage, setErrorMessage] = useState("");
  const [nominatedPlayer, setNominatedPlayer] = useState(null);
  const [nominationMatches, setNominationMatches] = useState([]);
  const [nominationListening, setNominationListening] = useState(false);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [nominationNoMatch, setNominationNoMatch] = useState(false);
  const [resetConfirmPending, setResetConfirmPending] = useState(false);
  const [resetBidInput, setResetBidInput] = useState("");

  // ── Refs ───────────────────────────────────────────────────────────
  const recognitionRef = useRef(null);
  const countdownTimeoutRef = useRef(null);
  const scheduleCountdownRef = useRef(() => {});
  const announcementTimeoutsRef = useRef([]);
  const announcementSessionRef = useRef(0);
  const bidFeedbackTimeoutRef = useRef(null);
  const listeningRef = useRef(false);
  const acceptingBidsRef = useRef(false);
  const lastBidRef = useRef(null);
  const listeningStartSessionRef = useRef(0);
  const nominationRecognitionRef = useRef(null);
  const nominationListeningRef = useRef(false);
  const lastInterimBidRef = useRef({ amount: null, timestamp: 0 });
  const pendingInterimBidRef = useRef(null);
  const auctionBidCountRef = useRef(0);
  const drainingRef = useRef(false);
  const drainResolveRef = useRef(null);

  // Keep lastBidRef in sync with state.
  useEffect(() => {
    lastBidRef.current = lastBid;
  }, [lastBid]);

  // ── Bootstrap ─────────────────────────────────────────────────────

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    pushDebugEvent("Environment detected", navigator.userAgent);
  }, [pushDebugEvent]);

  useEffect(() => {
    let cancelled = false;
    loadPlayers("NFL")
      .then(players => {
        if (!cancelled) {
          setPlayersLoaded(true);
          pushDebugEvent("Player data loaded", `${players.length} players`);
        }
      })
      .catch(err => {
        if (!cancelled) {
          pushDebugEvent("Player data load failed", String(err && err.message ? err.message : err));
        }
      });
    return () => { cancelled = true; };
  }, [pushDebugEvent]);

  // ── Timer / countdown helpers ─────────────────────────────────────

  const clearCountdown = useCallback(() => {
    if (countdownTimeoutRef.current) {
      clearTimeout(countdownTimeoutRef.current);
      countdownTimeoutRef.current = null;
    }
  }, []);

  const clearAnnouncementTimeouts = useCallback(() => {
    announcementTimeoutsRef.current.forEach(clearTimeout);
    announcementTimeoutsRef.current = [];
  }, []);

  const clearBidFeedbackTimeout = useCallback(() => {
    if (bidFeedbackTimeoutRef.current) {
      clearTimeout(bidFeedbackTimeoutRef.current);
      bidFeedbackTimeoutRef.current = null;
    }
  }, []);

  const clearPendingInterimBid = useCallback(() => {
    if (pendingInterimBidRef.current) {
      clearTimeout(pendingInterimBidRef.current.timeoutId);
      pendingInterimBidRef.current = null;
    }
  }, []);

  // ── Announcement control ──────────────────────────────────────────

  const cancelAnnouncement = useCallback(() => {
    announcementSessionRef.current += 1;
    clearAnnouncementTimeouts();
    clearBidFeedbackTimeout();
    clearVisualTimer();
    stopAuctionClip();
    window.speechSynthesis?.cancel();
    if (drainingRef.current) {
      drainingRef.current = false;
      if (drainResolveRef.current) {
        drainResolveRef.current();
        drainResolveRef.current = null;
      }
    }
  }, [clearAnnouncementTimeouts, clearBidFeedbackTimeout, clearVisualTimer, stopAuctionClip]);

  // ── Speech recognition control ────────────────────────────────────

  const stopRecognition = useCallback(() => {
    drainingRef.current = false;
    if (drainResolveRef.current) {
      drainResolveRef.current();
      drainResolveRef.current = null;
    }
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      try {
        if (typeof recognitionRef.current.abort === "function") {
          recognitionRef.current.abort();
        } else {
          recognitionRef.current.stop();
        }
      } catch {}
      recognitionRef.current = null;
    }
  }, []);

  const drainRecognition = useCallback(
    () =>
      new Promise(resolve => {
        if (!recognitionRef.current) {
          resolve();
          return;
        }

        drainingRef.current = true;
        drainResolveRef.current = resolve;

        const safetyTimeout = setTimeout(() => {
          if (drainingRef.current) {
            drainingRef.current = false;
            drainResolveRef.current = null;
            resolve();
          }
        }, DRAIN_TIMEOUT_MS);

        const prevResolve = drainResolveRef.current;
        drainResolveRef.current = () => {
          clearTimeout(safetyTimeout);
          prevResolve();
        };

        try {
          recognitionRef.current.stop();
        } catch {
          drainingRef.current = false;
          drainResolveRef.current = null;
          clearTimeout(safetyTimeout);
          resolve();
        }
      }),
    []
  );

  const stopListeningSession = useCallback(() => {
    listeningStartSessionRef.current += 1;
    setIsStartingListening(false);
    listeningRef.current = false;
    acceptingBidsRef.current = false;
    clearCountdown();
    clearVisualTimer();
    clearBidFeedbackTimeout();
    clearPendingInterimBid();
    cancelAnnouncement();
    stopRecognition();
    setListening(false);
    setResetConfirmPending(false);
  }, [cancelAnnouncement, clearBidFeedbackTimeout, clearCountdown, clearPendingInterimBid, clearVisualTimer, stopRecognition]);

  // ── Bid reset logic ───────────────────────────────────────────────

  const resetBid = useCallback(() => {
    cancelAnnouncement();
    clearCountdown();
    clearVisualTimer();
    clearBidFeedbackTimeout();
    setLastBid(null);
    lastBidRef.current = null;
    auctionBidCountRef.current = 0;
    setResetConfirmPending(false);
    setResetBidInput("");
    setUiState("resetting_bid");
    pushDebugEvent("Bid reset by commissioner");
  }, [cancelAnnouncement, clearBidFeedbackTimeout, clearCountdown, clearVisualTimer, pushDebugEvent]);

  const submitResetBid = useCallback(
    inputValue => {
      const amount = extractBidAmount(inputValue);
      if (amount === null || Number.isNaN(amount) || amount <= 0) {
        pushDebugEvent("Reset bid rejected (invalid)", `"${inputValue}"`);
        return;
      }
      if (amount > MAX_BID_AMOUNT) {
        pushDebugEvent("Reset bid rejected (exceeds max)", `${amount} > ${MAX_BID_AMOUNT}`);
        return;
      }
      auctionBidCountRef.current = 1;
      lastBidRef.current = amount;
      setLastBid(amount);
      setResetBidInput("");
      setUiState("bid_received");
      playBidDing();
      pushDebugEvent("Reset bid accepted", `${amount}`);
      clearBidFeedbackTimeout();
      bidFeedbackTimeoutRef.current = setTimeout(() => {
        if (listeningRef.current && acceptingBidsRef.current) {
          setUiState("listening");
        }
      }, BID_FEEDBACK_DISPLAY_MS);
      if (scheduleCountdownRef.current) {
        scheduleCountdownRef.current("bid_received");
      }
    },
    [clearBidFeedbackTimeout, playBidDing, pushDebugEvent]
  );

  // ── Nomination logic ──────────────────────────────────────────────

  const stopNominationRecognition = useCallback(() => {
    if (nominationRecognitionRef.current) {
      nominationRecognitionRef.current.onresult = null;
      nominationRecognitionRef.current.onerror = null;
      nominationRecognitionRef.current.onend = null;
      try {
        if (typeof nominationRecognitionRef.current.abort === "function") {
          nominationRecognitionRef.current.abort();
        } else {
          nominationRecognitionRef.current.stop();
        }
      } catch {}
      nominationRecognitionRef.current = null;
    }
    nominationListeningRef.current = false;
  }, []);

  const confirmNomination = useCallback(
    player => {
      stopNominationRecognition();
      setNominatedPlayer(player);
      setNominationMatches([]);
      setNominationNoMatch(false);
      setNominationListening(false);
      pushDebugEvent("Player nominated", `${player.name} (${player.position}, ${player.team})`);
    },
    [pushDebugEvent, stopNominationRecognition]
  );

  const handleNominationTranscript = useCallback(
    transcript => {
      const cleaned = (transcript || "").trim();
      if (cleaned.length < 2) {
        pushDebugEvent("Nomination transcript ignored (too short)", `"${cleaned}"`);
        return;
      }

      const results = searchPlayers(cleaned, 5);
      pushDebugEvent(
        "Nomination search",
        `"${cleaned}" → ${results.length} match(es)${results.length > 0 ? ` (best: ${results[0].score.toFixed(3)})` : ""}`
      );

      if (results.length === 0) {
        setNominationMatches([]);
        setNominationNoMatch(true);
        pushDebugEvent("Nomination no match", `"${cleaned}" below threshold`);
        return;
      }

      setNominationNoMatch(false);

      const best = results[0];
      const runnerUp = results[1];
      const isStrongMatch = best.score < 0.2;
      const hasSignificantGap = !runnerUp || runnerUp.score - best.score > 0.15;

      if (isStrongMatch && hasSignificantGap) {
        pushDebugEvent("Nomination auto-select", `${best.item.name} (score: ${best.score.toFixed(3)})`);
        confirmNomination(best.item);
        return;
      }

      setNominationMatches(results);
    },
    [confirmNomination, pushDebugEvent]
  );

  const startNominating = useCallback(() => {
    stopListeningSession();
    setNominatedPlayer(null);
    setNominationMatches([]);
    setNominationNoMatch(false);
    setLastBid(null);
    setUiState("nominating");
    setNominationListening(false);
    pushDebugEvent("Nomination started");
  }, [pushDebugEvent, stopListeningSession]);

  const toggleNominationListening = useCallback(() => {
    if (nominationListening) {
      stopNominationRecognition();
      setNominationListening(false);
      pushDebugEvent("Nomination mic stopped by user");
    } else {
      setNominationListening(true);
      pushDebugEvent("Nomination mic started by user");
    }
  }, [nominationListening, pushDebugEvent, stopNominationRecognition]);

  // Nomination speech recognition effect.
  useEffect(() => {
    if (!nominationListening) {
      stopNominationRecognition();
      return undefined;
    }

    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      pushDebugEvent("Speech recognition unsupported (nomination)");
      setNominationListening(false);
      return undefined;
    }

    nominationListeningRef.current = true;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = event => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (!result.isFinal) continue;
        pushDebugEvent("Nomination transcript received", result[0].transcript);
        handleNominationTranscript(result[0].transcript);
      }
    };

    recognition.onerror = event => {
      pushDebugEvent("Nomination recognition error", event.error);
      if (NON_FATAL_RECOGNITION_ERRORS.has(event.error)) return;
      setNominationListening(false);
      setErrorMessage("Speech recognition hit an error. Try typing a player name instead.");
    };

    let rapidRestartCount = 0;
    let lastRestartTime = 0;

    recognition.onend = () => {
      pushDebugEvent("Nomination recognition ended");
      if (!nominationListeningRef.current) return;

      const now = Date.now();
      if (now - lastRestartTime < RAPID_RESTART_THRESHOLD_MS) {
        rapidRestartCount += 1;
      } else {
        rapidRestartCount = 0;
      }
      lastRestartTime = now;

      if (rapidRestartCount >= MAX_RAPID_RESTARTS) {
        pushDebugEvent("Nomination recognition stopped (rapid restart loop detected)");
        nominationListeningRef.current = false;
        setNominationListening(false);
        setErrorMessage("Speech recognition keeps restarting. Try typing a player name instead.");
        return;
      }

      pushDebugEvent("Nomination recognition restarting");
      try {
        recognition.start();
      } catch (err) {
        pushDebugEvent("Nomination recognition restart failed", String(err && err.message ? err.message : err));
        nominationListeningRef.current = false;
        setNominationListening(false);
      }
    };

    pushDebugEvent("Nomination recognition started");
    recognition.start();
    nominationRecognitionRef.current = recognition;

    return () => {
      nominationListeningRef.current = false;
      if (nominationRecognitionRef.current) {
        nominationRecognitionRef.current.onresult = null;
        nominationRecognitionRef.current.onerror = null;
        nominationRecognitionRef.current.onend = null;
        try { nominationRecognitionRef.current.abort(); } catch {}
        nominationRecognitionRef.current = null;
      }
    };
  }, [nominationListening, handleNominationTranscript, pushDebugEvent, stopNominationRecognition]);

  // ── Auction listening ─────────────────────────────────────────────

  const beginListeningWithOpeningPrompt = useCallback(async () => {
    const sessionId = listeningStartSessionRef.current + 1;
    listeningStartSessionRef.current = sessionId;
    setIsStartingListening(true);
    pushDebugEvent("Opening prompt started");

    await playAuctionClip("opening-prompt", { useSpeechFallback: false });

    if (listeningStartSessionRef.current !== sessionId) return;

    pushDebugEvent("Opening prompt finished");
    setIsStartingListening(false);
    setListening(true);
  }, [playAuctionClip, pushDebugEvent]);

  const waitStep = useCallback(
    delay =>
      new Promise(resolve => {
        startVisualTimer(delay);
        const timeoutId = setTimeout(() => {
          clearVisualTimer();
          resolve();
        }, delay);
        announcementTimeoutsRef.current.push(timeoutId);
      }),
    [clearVisualTimer, startVisualTimer]
  );

  const startAnnouncement = useCallback(() => {
    const sessionId = announcementSessionRef.current + 1;
    announcementSessionRef.current = sessionId;
    pushDebugEvent("Announcement sequence started", `session ${sessionId}`);

    const runAnnouncement = async () => {
      const cancelled = () =>
        announcementSessionRef.current !== sessionId || !listeningRef.current;

      const restartRecognitionAfterDrain = () => {
        if (recognitionRef.current) {
          try {
            recognitionRef.current.start();
            pushDebugEvent("Recognition restarted after drain");
          } catch (err) {
            pushDebugEvent("Recognition restart after drain failed", String(err && err.message ? err.message : err));
          }
        }
      };

      // ── Drain before going-once ─────────────────────────────────
      pushDebugEvent("Draining speech engine before going-once");
      await drainRecognition();
      if (cancelled()) {
        pushDebugEvent("Announcement cancelled during pre-going-once drain", `session ${sessionId}`);
        return;
      }
      restartRecognitionAfterDrain();

      setUiState("going_once");
      pushDebugEvent("Announcement state changed", "going_once");
      await playAuctionClip("going-once");

      if (cancelled()) {
        pushDebugEvent("Announcement sequence cancelled", `session ${sessionId}`);
        return;
      }

      pushDebugEvent("Closing interval started", `${ANNOUNCEMENT_PAUSE_MS}ms`);
      await waitStep(ANNOUNCEMENT_PAUSE_MS);

      if (cancelled()) {
        pushDebugEvent("Announcement sequence cancelled", `session ${sessionId}`);
        return;
      }

      // ── Drain before going-twice ────────────────────────────────
      pushDebugEvent("Draining speech engine before going-twice");
      await drainRecognition();
      if (cancelled()) {
        pushDebugEvent("Announcement cancelled during pre-going-twice drain", `session ${sessionId}`);
        return;
      }
      restartRecognitionAfterDrain();

      setUiState("going_twice");
      pushDebugEvent("Announcement state changed", "going_twice");
      await playAuctionClip("going-twice");

      if (cancelled()) {
        pushDebugEvent("Announcement sequence cancelled", `session ${sessionId}`);
        return;
      }

      pushDebugEvent("Closing interval started", `${ANNOUNCEMENT_PAUSE_MS}ms`);
      await waitStep(ANNOUNCEMENT_PAUSE_MS);

      if (cancelled()) {
        pushDebugEvent("Announcement sequence cancelled", `session ${sessionId}`);
        return;
      }

      // ── Drain before sold ───────────────────────────────────────
      pushDebugEvent("Draining speech engine before sold");
      await drainRecognition();
      if (cancelled()) {
        pushDebugEvent("Announcement cancelled during pre-sold drain", `session ${sessionId}`);
        return;
      }

      acceptingBidsRef.current = false;
      setUiState("sold");
      pushDebugEvent("Announcement state changed", "sold");
      stopRecognition();
      await playAuctionClip("sold");

      if (announcementSessionRef.current === sessionId && listeningRef.current) {
        stopListeningSession();
      }
    };

    runAnnouncement();
  }, [drainRecognition, playAuctionClip, pushDebugEvent, stopListeningSession, stopRecognition, waitStep]);

  const scheduleCountdown = useCallback(
    (nextUiState = "listening") => {
      clearCountdown();

      const hotExtension =
        auctionBidCountRef.current >= HOT_AUCTION_BID_THRESHOLD
          ? HOT_AUCTION_EXTENSION_MS
          : 0;
      const visualWindow = BID_WINDOW_MS + hotExtension;

      startVisualTimer(visualWindow);
      setUiState(nextUiState);
      pushDebugEvent(
        "Bid window started",
        `${visualWindow}ms${hotExtension ? ` (hot +${hotExtension}ms)` : ""}`
      );

      countdownTimeoutRef.current = setTimeout(() => {
        countdownTimeoutRef.current = null;
        if (listeningRef.current && acceptingBidsRef.current) {
          startAnnouncement();
        }
      }, visualWindow);
    },
    [clearCountdown, pushDebugEvent, startAnnouncement, startVisualTimer]
  );

  // Keep the ref current via an effect (not during render).
  useEffect(() => {
    scheduleCountdownRef.current = scheduleCountdown;
  }, [scheduleCountdown]);

  const handleRecognizedTranscript = useCallback(
    transcript => {
      if (!acceptingBidsRef.current) return;

      let processedTranscript = transcript;
      const normalizedForStutter = normalizeTranscript(transcript);
      if (
        REPEATED_DIGIT_PATTERN.test(normalizedForStutter) ||
        REPEATED_WORD_PATTERN.test(normalizedForStutter)
      ) {
        processedTranscript = deduplicateRepeatedDigits(normalizedForStutter);
        pushDebugEvent("Collapsed repeated digit", `"${transcript}" → "${processedTranscript}"`);
      }

      const bidAmounts = extractAllBidAmounts(processedTranscript);
      if (bidAmounts.length === 0) return;

      if (bidAmounts.length > 1) {
        pushDebugEvent("Split into separate bids", bidAmounts.join(", "));
      }

      for (const rawAmount of bidAmounts) {
        if (rawAmount > MAX_BID_AMOUNT) {
          pushDebugEvent("Bid rejected (exceeds max)", `${rawAmount} > ${MAX_BID_AMOUNT}`);
          continue;
        }

        // Apply the single-digit shorthand rule and resolve ambiguous
        // doubled-digit amounts ("44" = forty-four or two owners saying
        // "four") against the current bid.
        const bidAmount = resolveBidAmount(rawAmount, lastBidRef.current);
        if (bidAmount === null) {
          continue;
        }
        if (bidAmount !== rawAmount) {
          pushDebugEvent("Bid interpreted", `${rawAmount} → ${bidAmount}`);
        }

        cancelAnnouncement();
        acceptingBidsRef.current = true;
        auctionBidCountRef.current += 1;
        setResetConfirmPending(false);
        playBidDing();
        lastBidRef.current = bidAmount;
        setLastBid(bidAmount);
        setUiState("bid_received");
        pushDebugEvent("Bid accepted", `${bidAmount} (bid #${auctionBidCountRef.current})`);
        clearBidFeedbackTimeout();
        bidFeedbackTimeoutRef.current = setTimeout(() => {
          if (listeningRef.current && acceptingBidsRef.current) {
            setUiState("listening");
          }
        }, BID_FEEDBACK_DISPLAY_MS);
        scheduleCountdownRef.current("bid_received");
      }
    },
    [cancelAnnouncement, clearBidFeedbackTimeout, playBidDing, pushDebugEvent]
  );

  // Main speech recognition effect.
  useEffect(() => {
    if (!listening) {
      stopListeningSession();
      return undefined;
    }

    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setErrorMessage("");
      setUiState("unsupported");
      setListening(false);
      pushDebugEvent("Speech recognition unsupported");
      return undefined;
    }

    listeningRef.current = true;
    acceptingBidsRef.current = true;
    setErrorMessage("");
    setUiState("waiting_opening_bid");

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = event => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];

        if (result.isFinal) {
          // The final transcript supersedes any buffered interim bid.
          clearPendingInterimBid();
          pushDebugEvent("Transcript received", result[0].transcript);
          handleRecognizedTranscript(result[0].transcript);
          lastInterimBidRef.current = { amount: null, timestamp: 0 };
          continue;
        }

        if (!acceptingBidsRef.current) continue;

        const interimText = result[0].transcript;
        const interimAmounts = extractAllBidAmounts(interimText);
        if (interimAmounts.length === 0) continue;

        const bestInterim = Math.max(...interimAmounts);
        const now = Date.now();
        const lastInterim = lastInterimBidRef.current;

        if (
          bestInterim === lastInterim.amount &&
          now - lastInterim.timestamp < INTERIM_BID_COOLDOWN_MS
        ) {
          continue;
        }

        if (lastBidRef.current !== null && bestInterim <= lastBidRef.current) {
          continue;
        }

        lastInterimBidRef.current = { amount: bestInterim, timestamp: now };

        // Buffer the interim bid briefly instead of committing right away,
        // so a refinement like "40" → "44" lands as one bid, not two.
        const pending = pendingInterimBidRef.current;
        if (pending && bestInterim <= pending.amount) continue;
        if (pending) clearTimeout(pending.timeoutId);

        const timeoutId = setTimeout(() => {
          const due = pendingInterimBidRef.current;
          pendingInterimBidRef.current = null;
          if (!due || !(listeningRef.current && acceptingBidsRef.current)) return;
          pushDebugEvent("Interim bid committed", `${due.amount} from "${due.transcript}"`);
          handleRecognizedTranscript(due.transcript);
        }, INTERIM_BID_COMMIT_DELAY_MS);

        pendingInterimBidRef.current = { transcript: interimText, amount: bestInterim, timeoutId };
        pushDebugEvent("Interim bid buffered", `${bestInterim} from "${interimText}"`);
      }
    };

    recognition.onerror = event => {
      pushDebugEvent("Speech recognition error", event.error);
      if (NON_FATAL_RECOGNITION_ERRORS.has(event.error)) {
        if (listeningRef.current && acceptingBidsRef.current) {
          setErrorMessage("");
          setUiState(prev => {
            if (prev === "resetting_bid") return "resetting_bid";
            return lastBidRef.current === null ? "waiting_opening_bid" : "listening";
          });
        }
        return;
      }

      setErrorMessage("Speech recognition hit an error. Tap start to try again.");
      setUiState("error");
      stopListeningSession();
    };

    let bidRapidRestartCount = 0;
    let bidLastRestartTime = 0;

    recognition.onend = () => {
      pushDebugEvent("Speech recognition ended");

      if (drainingRef.current) {
        pushDebugEvent("Drain completed — engine flushed");
        drainingRef.current = false;
        if (drainResolveRef.current) {
          drainResolveRef.current();
          drainResolveRef.current = null;
        }
        return;
      }

      if (!(listeningRef.current && acceptingBidsRef.current)) return;

      const now = Date.now();
      if (now - bidLastRestartTime < RAPID_RESTART_THRESHOLD_MS) {
        bidRapidRestartCount += 1;
      } else {
        bidRapidRestartCount = 0;
      }
      bidLastRestartTime = now;

      if (bidRapidRestartCount >= MAX_RAPID_RESTARTS) {
        pushDebugEvent("Speech recognition stopped (rapid restart loop detected)");
        setErrorMessage("Speech recognition keeps restarting. Tap start to try again.");
        setUiState("error");
        stopListeningSession();
        return;
      }

      pushDebugEvent("Speech recognition restarting");
      try {
        recognition.start();
      } catch (err) {
        pushDebugEvent("Speech recognition restart failed", String(err && err.message ? err.message : err));
        setErrorMessage("Speech recognition failed to restart. Tap start to try again.");
        setUiState("error");
        stopListeningSession();
      }
    };

    pushDebugEvent("Speech recognition started");
    recognition.start();
    recognitionRef.current = recognition;

    return () => {
      listeningRef.current = false;
      acceptingBidsRef.current = false;
      clearCountdown();
      clearBidFeedbackTimeout();
      clearPendingInterimBid();
      clearVisualTimer();
      cancelAnnouncement();
      stopRecognition();
    };
  }, [
    cancelAnnouncement,
    clearBidFeedbackTimeout,
    clearCountdown,
    clearPendingInterimBid,
    clearVisualTimer,
    handleRecognizedTranscript,
    listening,
    stopListeningSession,
    stopRecognition,
    pushDebugEvent,
  ]);

  // ── User actions ──────────────────────────────────────────────────

  const toggleListening = useCallback(() => {
    if (listening) {
      stopListeningSession();
      setUiState("idle");
      setErrorMessage("");
      return;
    }

    if (isStartingListening) return;

    stopNominationRecognition();
    setNominationListening(false);
    setNominationMatches([]);

    setLastBid(null);
    auctionBidCountRef.current = 0;
    clearVisualTimer();
    setErrorMessage("");
    resetClipAvailability();
    unlockAudioContext();
    beginListeningWithOpeningPrompt();
  }, [
    beginListeningWithOpeningPrompt,
    clearVisualTimer,
    isStartingListening,
    listening,
    resetClipAvailability,
    stopListeningSession,
    stopNominationRecognition,
    unlockAudioContext,
  ]);

  const handlePrimaryAction = useCallback(() => {
    if (uiState === "nominating") {
      if (nominatedPlayer) {
        toggleListening();
      } else {
        toggleListening();
        pushDebugEvent("Nomination skipped — starting auction");
      }
      return;
    }

    if (uiState === "sold") {
      startNominating();
      return;
    }

    toggleListening();
  }, [nominatedPlayer, pushDebugEvent, startNominating, toggleListening, uiState]);

  const handleTestAudio = useCallback(() => {
    pushDebugEvent("Test audio requested");
    unlockAudioContext();
    playAuctionClip("going-once", { useSpeechFallback: false });
  }, [playAuctionClip, pushDebugEvent, unlockAudioContext]);

  // ── Derived render values ─────────────────────────────────────────

  const isSupported = Boolean(getSpeechRecognition());
  const activeUiState = isSupported ? uiState : "unsupported";
  const hasAcceptedBid = lastBid !== null;
  const formattedBid = hasAcceptedBid ? formatBidAmount(lastBid) : null;
  const stateTone = STATE_TONE_MAP[activeUiState] || "idle";
  const isActiveBidding = !["nominating", "idle", "error", "unsupported"].includes(activeUiState);

  // Build the dynamic parts of UI copy that depend on component state.
  const currentUi = STATIC_UI_STATE_COPY[activeUiState] || (() => {
    switch (activeUiState) {
      case "nominating":
        return {
          badgeText: nominatedPlayer ? "SELECTED" : "NOMINATE",
          headlineText: nominatedPlayer
            ? ""
            : "Who’s On The Block?",
          subtext: nominatedPlayer
            ? ""
            : nominationMatches.length > 0
              ? "Select a player below, or say another name."
              : nominationNoMatch
                ? "No match found. Try again or type a name below."
                : playersLoaded
                  ? "Type a name or tap the mic to speak."
                  : "Loading player data…",
          timerLabel: "Nomination",
          timerCaption: nominatedPlayer
            ? "Player selected. Ready to start."
            : "Listening for a player name.",
          primaryButtonText: nominatedPlayer
            ? "Start Listening"
            : "Skip Nomination",
        };
      case "idle":
        return {
          badgeText: "READY",
          headlineText: nominatedPlayer ? "Ready To Bid" : "Tap To Open Bidding",
          subtext: nominatedPlayer
            ? `${nominatedPlayer.name} is on the block. Start the auctioneer to begin.`
            : "Start the auctioneer to begin listening for spoken bids.",
          timerLabel: "Bid Window",
          timerCaption: "Timer starts after the opening bid.",
          primaryButtonText: "Start Listening",
        };
      case "bid_received":
        return {
          badgeText: "BID HEARD",
          headlineText: "Bid Accepted",
          subtext: lastBid === null
            ? "Auction timer restarted."
            : `${formatBidAmount(lastBid)} accepted. Auction timer restarted.`,
          timerLabel: "New Bid Window",
          timerCaption: "Waiting for next higher bid.",
          primaryButtonText: "Stop Listening",
        };
      case "sold":
        return {
          badgeText: "SOLD",
          headlineText: "",
          subtext: nominatedPlayer ? `Sold for ${formattedBid || "$0"}.` : "This player has been sold.",
          timerLabel: "Auction Complete",
          timerCaption: "Bidding has ended.",
          primaryButtonText: "Next Player",
        };
      case "error":
        return {
          ...STATIC_UI_STATE_COPY.error,
          subtext: errorMessage || "Microphone or speech recognition encountered an error.",
        };
      default:
        return STATIC_UI_STATE_COPY.idle || { badgeText: "", headlineText: "", subtext: "", timerLabel: "", timerCaption: "", primaryButtonText: "" };
    }
  })();

  const currentBidDisplay = STATIC_BID_DISPLAY[activeUiState] || (() => {
    switch (activeUiState) {
      case "nominating":
        return { bidLabel: "Status", bidValue: nominationListening ? "Listening…" : "Ready" };
      case "idle":
      case "error":
        return { bidLabel: "Last Bid", bidValue: formattedBid || "No bids yet" };
      case "sold":
        return { bidLabel: "Sold At", bidValue: formattedBid || "No bids yet" };
      default:
        return { bidLabel: "Current Bid", bidValue: formattedBid || "Opening Bid Needed" };
    }
  })();

  const currentFooterCopy = STATIC_FOOTER_COPY[activeUiState] || (() => {
    switch (activeUiState) {
      case "nominating":
        return nominatedPlayer
          ? {
              footerLabel: "Ready",
              footerMessage: "Tap Start Listening to open bidding, or tap the player card to change your pick.",
            }
          : {
              footerLabel: "How It Works",
              footerMessage: "Type a player’s name or tap the mic to speak, or skip to start bidding without a nomination.",
            };
      case "idle":
        return {
          footerLabel: "How To Use",
          footerMessage: "Say a bid like 20 or 20 dollars once bidding is open.",
        };
      case "sold":
        return {
          footerLabel: "Next Player",
          footerMessage: "Tap Next Player to nominate the next player for auction.",
        };
      default:
        return { footerLabel: "", footerMessage: "" };
    }
  })();

  const timerProgress = timerTotalMs === 0
    ? 0
    : Math.max(0, Math.min(1, timerRemainingMs / timerTotalMs));
  const buttonDisabled = activeUiState === "unsupported" || isStartingListening;
  const timerIsActive = timerRemainingMs > 0 && (
    activeUiState === "listening" ||
    activeUiState === "bid_received" ||
    activeUiState === "going_once" ||
    activeUiState === "going_twice"
  );
  const timerSecondsText = timerIsActive
    ? `${(timerRemainingMs / 1000).toFixed(1)}s remaining`
    : "Waiting for countdown";

  // ── CSS class names ───────────────────────────────────────────────

  const headerClassName = `auctioneer-header${isActiveBidding ? " auctioneer-header--compact" : ""}`;
  const boardClassName = `auction-board auction-board--${stateTone}${isActiveBidding ? " auction-board--hero-bid" : ""}${isActiveBidding && nominatedPlayer ? " auction-board--two-panel" : ""}`;
  const badgeClassName = `auction-board__badge auction-board__badge--${stateTone}`;
  const buttonClassName = `auction-controls__button auction-controls__button--${stateTone}`;
  const footerClassName = `auctioneer-footer auctioneer-footer--${stateTone}`;
  const timerTrackClassName = timerIsActive
    ? "auction-board__timer-placeholder"
    : "auction-board__timer-placeholder auction-board__timer-placeholder--inactive";
  const timerValueClassName = timerIsActive
    ? "auction-board__timer-value"
    : "auction-board__timer-value auction-board__timer-value--inactive";
  const timerCaptionClassName = timerIsActive
    ? "auction-board__timer-caption"
    : "auction-board__timer-caption auction-board__timer-caption--inactive";

  const backdropStyle = {
    backgroundImage:
      "linear-gradient(rgba(13, 18, 25, 0.55), rgba(13, 18, 25, 0.72)), url('/assets/Auctioneer_On_Stage.png')",
  };
  const timerFillStyle = { transform: `scaleX(${timerProgress})` };

  // ── JSX ───────────────────────────────────────────────────────────

  return (
    <div className="auctioneer-shell">
      <div className="auctioneer-shell__backdrop" style={backdropStyle} />

      <main className="auctioneer-shell__main">
        <header className={headerClassName}>
          <div className="auctioneer-header__eyebrow">Live Draft Bidding</div>
          <h1 className="auctioneer-header__title">Fantasy Auctioneer</h1>
          <p className="auctioneer-header__subtitle">
            A classic live-auction experience for fantasy sports.
          </p>
        </header>

        <section className={boardClassName} aria-live="polite">
          <div className="auction-board__inner">
            <div className="auction-board__status-block">
              <div className={badgeClassName}>{currentUi.badgeText}</div>
              {currentUi.headlineText && <h2 className="auction-board__headline">{currentUi.headlineText}</h2>}
              {currentUi.subtext && <p className="auction-board__message">{currentUi.subtext}</p>}
            </div>

            {activeUiState === "nominating" ? (
              <div className="nomination-matches">
                {nominatedPlayer ? (
                  <button
                    type="button"
                    className="nomination-selected-player"
                    onClick={() => {
                      setNominatedPlayer(null);
                      pushDebugEvent("Player deselected", nominatedPlayer.name);
                    }}
                    aria-label={`Deselect ${nominatedPlayer.name}. Tap to choose a different player.`}
                  >
                    <div className="nomination-selected-player__name">{nominatedPlayer.name}</div>
                    <div className="nomination-selected-player__meta">
                      {nominatedPlayer.position} &middot; {nominatedPlayer.team}
                    </div>
                    <div className="nomination-selected-player__hint">Tap to change</div>
                  </button>
                ) : (
                  <>
                    {nominationMatches.length > 0 ? (
                      <>
                        <div className="nomination-matches__label">Matches</div>
                        <ul className="nomination-matches__list">
                          {nominationMatches.map((match, index) => (
                            <li key={match.item.id}>
                              <button
                                type="button"
                                className={`nomination-matches__option${index === 0 ? " nomination-matches__option--best" : ""}`}
                                onClick={() => confirmNomination(match.item)}
                              >
                                <span className="nomination-matches__name">{match.item.name}</span>
                                <span className="nomination-matches__meta">{match.item.position} · {match.item.team}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <div className="nomination-matches__empty">
                        {nominationNoMatch ? (
                          <div className="nomination-matches__no-match">
                            No matching player found. Try again or type a name below.
                          </div>
                        ) : nominationListening ? (
                          <div className="nomination-matches__listening-indicator">
                            <span className="nomination-matches__mic-dot" />
                            Listening for a player name
                          </div>
                        ) : null}
                      </div>
                    )}
                    <PlayerSearch
                      onSelect={confirmNomination}
                      disabled={!playersLoaded}
                      micListening={nominationListening}
                      onMicToggle={toggleNominationListening}
                      micDisabled={!playersLoaded}
                    />
                  </>
                )}
              </div>
            ) : (
              <>
                {nominatedPlayer ? (
                  <div className="auction-board__stage-panel">
                    <div className="auction-board__player-block">
                      <div className="auction-board__player-name">{nominatedPlayer.name}</div>
                      <div className="auction-board__player-meta">
                        {nominatedPlayer.position} &middot; {nominatedPlayer.team}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="auction-board__live-panel">
                  <div className="auction-board__bid-block">
                    <div className="auction-board__bid-label">{currentBidDisplay.bidLabel}</div>
                    {activeUiState === "resetting_bid" ? (
                      <div className="auction-board__reset-entry">
                        <div className="auction-board__reset-prompt">Type or speak correct current bid</div>
                        <form
                          className="auction-board__reset-form"
                          onSubmit={e => {
                            e.preventDefault();
                            submitResetBid(resetBidInput);
                          }}
                        >
                          <span className="auction-board__reset-dollar">$</span>
                          <input
                            type="number"
                            className="auction-board__reset-input"
                            placeholder="0"
                            value={resetBidInput}
                            onChange={e => setResetBidInput(e.target.value)}
                            min="1"
                            max={MAX_BID_AMOUNT}
                            autoFocus
                          />
                          <button
                            type="submit"
                            className="auction-board__reset-submit"
                            disabled={!resetBidInput}
                          >
                            Set Bid
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="auction-board__bid-row">
                        <div className={`auction-board__bid-value${currentBidDisplay.bidValue.startsWith("$") ? "" : " auction-board__bid-value--text"}`}>{currentBidDisplay.bidValue}</div>
                        {hasAcceptedBid && listening ? (
                          <button
                            type="button"
                            className={`auction-board__reset-button${resetConfirmPending ? " auction-board__reset-button--confirm" : ""}`}
                            onClick={() => {
                              if (resetConfirmPending) {
                                resetBid();
                              } else {
                                setResetConfirmPending(true);
                                pushDebugEvent("Reset bid confirmation shown");
                              }
                            }}
                          >
                            {resetConfirmPending ? "Reset current bid?" : "Reset Bid"}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {activeUiState !== "sold" && (
                    <div className="auction-board__timer-block">
                      <div className="auction-board__timer-label">{currentUi.timerLabel}</div>
                      <div className={timerValueClassName}>{timerSecondsText}</div>
                      <div className={timerTrackClassName}>
                        <div className="auction-board__timer-fill" style={timerFillStyle} />
                      </div>
                      <div className={timerCaptionClassName}>{currentUi.timerCaption}</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="auction-controls">
          <button
            className={buttonClassName}
            type="button"
            onClick={handlePrimaryAction}
            disabled={buttonDisabled}
          >
            {isStartingListening ? "Starting..." : currentUi.primaryButtonText}
          </button>
          {activeUiState === "idle" && nominatedPlayer ? (
            <button
              className="auction-controls__secondary"
              type="button"
              onClick={() => {
                pushDebugEvent("Re-nomination requested", nominatedPlayer.name);
                startNominating();
              }}
            >
              Change Player
            </button>
          ) : null}
        </section>

        <footer className={footerClassName}>
          <div className="auctioneer-footer__label">{currentFooterCopy.footerLabel}</div>
          <p className="auctioneer-footer__instruction">{currentFooterCopy.footerMessage}</p>
        </footer>

        {debugMode ? (
          <section className="auction-debug" aria-live="polite">
            <div className="auction-debug__label">Debug Trace</div>
            <div className="auction-debug__actions">
              <button
                type="button"
                className="auction-debug__test-button"
                onClick={handleTestAudio}
                disabled={listening || isStartingListening}
              >
                Test audio
              </button>
            </div>
            <ul className="auction-debug__list">
              {debugEvents.map(entry => (
                <li key={entry.id} className="auction-debug__item">{entry.text}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
    </div>
  );
}

